
import { CaptchaFormMessage } from '@eliadmin/core/src/components/providers/captcha-form-message';
import { useCart } from '@eliadmin/core/src/hooks/cart/use-cart';
import { useCartPaymentsState } from '@eliadmin/core/src/hooks/cart/use-cart-payments-state';
import { useCartShipments } from '@eliadmin/core/src/hooks/cart/use-cart-shipments';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import type { CreateOrderFormValues } from '@eliadmin/core/src/pages/cart/page';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCountries } from '@eliadmin/core/src/api/general/general-api';
import { useFormContext } from 'react-hook-form';
import { Button } from '../../ui/button';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { createOrder } from '@eliadmin/core/src/api/cart/cart-api';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { env } from '@eliadmin/core/src/env';
import { CartStep } from '@eliadmin/core/src/hooks/cart/use-cart-stepper';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { FormField, FormItem, FormControl, FormLabel } from '@eliadmin/core/src/components/ui/form';

import { CartSummaryCard } from '@eliadmin/core/src/components/cart/cart-summary-card';
import { Field } from '../../form/field';
import { CartSection } from '../cart-section';
import { SummaryCardItem } from '../order-shipment/summary-card-item';
import { CartNavigationButton } from '../continue-button';
import { Fragment, useMemo } from 'react';
import { isNullish } from '@eliadmin/core/src/utils/misc-utils';
import { Heading } from '../../ui/heading';
import {
  GdprCartSummary,
  getGdprErrorText,
} from '@eliadmin/core/src/utils/gdpr-tos/gdpr-cart-summary';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';
import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';

export const CartSummary = () => {
  const { t } = useTranslations();

  const locale = useLocaleStore(state => state.currentLocale);
  const { me } = useCustomerStore();
  const paymentState = useCartPaymentsState();
  const { data: payments } = paymentState;
  const queryClient = useQueryClient();
  const pendingShipments = useIsMutating({ mutationKey: [CacheKey.SHIPMENTS] });
  const pendingPayments = useIsMutating({ mutationKey: [CacheKey.CART_PAYMENTS] });
  const { data: shipments } = useCartShipments();
  const generalLayout = useGlobalStore(state => state.generalLayout);

  const { toast } = useToast();

  const { data: cart } = useCart();

  const createOrderMutation = useMutation({ mutationFn: createOrder });

  const form = useFormContext<CreateOrderFormValues>();

  const heureka_connected = !!generalLayout.marketing?.heureka_customer_verification_id;

  const handleSubmit = form.handleSubmit(async data => {
    const checkoutStillReady = paymentState.captureCheckoutGuard(cart.paymentType?.id);
    const mutationsPending = () =>
      queryClient.isMutating({ mutationKey: [CacheKey.SHIPMENTS] }) ||
      queryClient.isMutating({ mutationKey: [CacheKey.CART_PAYMENTS] });
    if (mutationsPending() || !checkoutStillReady()) return;
    // Check if GDPR is accepted
    if (!data.gdpr) {
      form.setError('gdpr', { type: 'manual', message: getGdprErrorText(locale) });
      toast({
        title: '',
        variant: 'destructive',
        description: getGdprErrorText(locale),
      });
      return;
    }

    data.return_url = new URL(ROUTES.ORDER_CONFIRM(), env.NEXT_PUBLIC_APP_URL).toString();
    if (mutationsPending() || !checkoutStillReady()) return;
    try {
      const res = await createOrderMutation.mutateAsync({
        locale,
        body: {
          ...data,
          send_heureka_mail: data.send_heureka_mail,
        },
        authToken: me?.authToken,
      });

      if (res.is_form && res.method === 'POST') {
        const url = res.redirect;
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = url;
        for (const [key, value] of Object.entries(res.form)) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = value as any;
          form.appendChild(input);
        }
        document.body.appendChild(form);
        form.submit();
      } else {
        window.location.href = res?.redirect ?? '/';
      }
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.log(e);

      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }

        const messages = Object.values(e.fields).flat().join('\n');
        toast({ title: t('text594'), description: messages });
        return;
      }

      // Network/5xx errors used to be swallowed, leaving the submit button silently inert.
      toast({ title: t('text594'), description: t('text530'), variant: 'destructive' });
    }
  });

  const { data: countries } = useQuery({
    queryKey: [CacheKey.COUNTRIES, locale],
    queryFn: () => fetchCountries({ locale }),
    refetchOnWindowFocus: false,
  });

  const countryName = (id: unknown) => {
    const country = countries?.data?.find(c => String(c.id) === String(id));
    return country ? t(country.name) : id;
  };

  const activePaymentId = cart.paymentType?.id;
  const activeShipmentId = cart.shipment?.id;

  const activePayment = useMemo(() => {
    return payments?.find(payment => String(payment.id) === String(activePaymentId)) ?? null;
  }, [activePaymentId, payments]);

  const activeShipment = useMemo(() => {
    if (isNullish(activeShipmentId)) return null;

    return shipments?.find(shipment => String(shipment.id) === String(activeShipmentId)) ?? null;
  }, [activeShipmentId, shipments]);

  const safeValue = (value: unknown) =>
    value && String(value).trim().length > 0 ? String(value) : '-';

  const summarySections = [
    {
      title: t('text2390'),
      fields: [
        { label: t('text1068'), value: safeValue(activePayment?.name) },
        { label: t('text125'), value: safeValue(activeShipment?.name) },
      ],
    },
    {
      title: t('text360'),
      fields: [
        { label: 'E-mail', value: safeValue(form.getValues('email')) },
        { label: t('text36'), value: safeValue(form.getValues('phone')) },
        { label: t('text33'), value: safeValue(form.getValues('name')) },
        { label: t('text34'), value: safeValue(form.getValues('surname')) },
      ],
    },

    {
      title: t('text45'),
      fields: [
        { label: t('text38'), value: safeValue(form.getValues('address')) },
        { label: t('text438'), value: safeValue(form.getValues('street_number')) },
        { label: t('text40'), value: safeValue(form.getValues('postcode')) },
        { label: t('text39'), value: safeValue(form.getValues('city')) },
        { label: t('text519'), value: safeValue(countryName(form.getValues('country.id'))) },
      ],
    },
    ...(form.getValues('different_delivery_address')
      ? [
          {
            title: t('text2139'),
            fields: [
              { label: t('text38'), value: safeValue(form.getValues('address2')) },
              { label: t('text438'), value: safeValue(form.getValues('street_number2')) },
              { label: t('text40'), value: safeValue(form.getValues('postcode2')) },
              { label: t('text39'), value: safeValue(form.getValues('city2')) },
              {
                label: t('text602'),
                value: safeValue(countryName(form.getValues('country2.id'))),
              },
            ],
          },
        ]
      : []),
    ...(form.getValues('company_buy')
      ? [
          {
            title: t('text679'),
            fields: [
              { label: t('text1188'), value: safeValue(form.getValues('company')) },
              { label: t('text42'), value: safeValue(form.getValues('ico')) },
              { label: t('text43'), value: safeValue(form.getValues('dic')) },
              { label: t('text44'), value: safeValue(form.getValues('ic_dph')) },
            ],
          },
        ]
      : []),
  ];

  const isDesktop = useBreakpoint('sm');

  return (
    <CartSection title={t('text169')}>
<CaptchaFormMessage errors={form.formState.errors} />
      {paymentState.isError && (
        <div role="alert" className="custom-container">
          <p>{t('text1068')}: {t('text530')}</p>
          <Button type="button" disabled={paymentState.fetchStatus !== 'idle'} onClick={() => paymentState.refetch()}>
            {t('text595')}
          </Button>
        </div>
      )}
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-8 pr-4 sm:pr-0 md:flex-row md:justify-between lg:gap-6',
        )}
      >
        <div className="flex w-full max-w-[807px] flex-col items-start justify-start gap-4 sm:gap-5 lg:gap-6">
          {summarySections.map(section => (
            <Fragment key={section.title}>
              <div className="flex w-full flex-col gap-4">
                <Heading title={section.title} tag="h4" />
                <div className="flex w-full flex-col items-start justify-start gap-2">
                  {section.fields.map((field, index) => (
                    <div key={index} className="grid w-full grid-cols-2 gap-6">
                      <Paragraph color="muted-foreground" size="lg" className="col-span-1 w-full">
                        {field.label}:
                      </Paragraph>
                      <Paragraph color="black" size="lg" className="col-span-1 w-full">
                        {field?.value ?? '-'}
                      </Paragraph>
                    </div>
                  ))}
                </div>
              </div>
              <Separator />
            </Fragment>
          ))}
          <div className="flex flex-col items-start justify-start gap-4">
            <FormField
              control={form.control}
              name="gdpr"
              render={({ field }) => (
                <FormItem
                  className={cn(
                    'flex flex-row items-start space-x-3 space-y-0',
                    'ea-cart-order-form__gdpr-field',
                  )}
                  required
                >
                  <FormControl>
                    <Checkbox
                      ref={field.ref}
                      name={field.name}
                      disabled={field.disabled}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormLabel className="pl-[4px] pt-[3px]">
                    <GdprCartSummary locale={locale} />
                  </FormLabel>
                </FormItem>
              )}
            />
            {heureka_connected && (
              <Field
                control={form.control}
                name="send_heureka_mail"
                type="checkbox"
                label={t('text2414')}
              />
            )}
          </div>
        </div>
        <div className="flex w-full flex-col items-start justify-start gap-6 rounded-[4px] border border-border bg-transparent p-3 sm:p-4 md:max-w-[569px] lg:p-6">
          <Heading title={t('text2138')} tag="h4" />
          {cart.items?.map(item => (
            <SummaryCardItem key={item.id} item={item} />
          ))}
          <Separator />
          <CartSummaryCard className={cn('border-none bg-transparent p-0 sm:p-0 md:p-0 lg:p-0')} />
        </div>
      </div>
      <div
        className={cn(
          'custom-container',
          'flex flex-col-reverse items-center justify-end gap-3 sm:flex-row sm:justify-end sm:pr-0',
        )}
      >
        <CartNavigationButton
          step={CartStep.ORDER}
          title={t('text1026')}
          variant={'secondary'}
          beforeIcon
        />
        <Button
          onClick={handleSubmit}
          disabled={createOrderMutation.isPending || pendingShipments > 0 || pendingPayments > 0 ||
            !paymentState.isPaymentReady(cart.paymentType?.id)}
          isLoading={createOrderMutation.isPending}
          className="w-full whitespace-nowrap sm:w-fit"
        >
          {t('text1394')}
        </Button>
      </div>
    </CartSection>
  );
};
export default CartSummary;

function Separator() {
  return <div className={cn('h-[1px] w-full bg-border')} />;
}
